import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import RegisterModal from '../../components/Auth/RegisterModal';
import LoginModal from '../../components/Auth/LoginModal';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import usePickerLanguage from '../ReadingTypes/usePickerLanguage';
import { ensureTarotScripts, tarotScriptsReady } from '../ReadingTypes/loadTarotScripts';
import '../FreeTarot/FreeTarot.css';

const ThreeCardTarot = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  usePickerLanguage('threeCard');

  useEffect(() => {
    if (!document.getElementById('tarot-reader-styles')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/tarot-reader/cards-only.css';
      link.id = 'tarot-reader-styles';
      document.head.appendChild(link);
    }

    let cancelled = false;
    const alreadyLoaded = tarotScriptsReady();
    const autoSelectPPF = () => {
      const waitForApp = setInterval(() => {
        if (cancelled || (window.tarotApp && typeof window.tarotApp.selectSpread === 'function')) {
          clearInterval(waitForApp);
          if (!cancelled && window.tarotApp) window.tarotApp.selectSpread('pastPresentFuture');
        }
      }, 100);
      setTimeout(() => clearInterval(waitForApp), 5000);
    };

    ensureTarotScripts()
      .then(() => {
        if (cancelled) return;
        if (alreadyLoaded) window.initTarotApp();
        autoSelectPPF();
      })
      .catch((error) => {
        console.error('Failed to load tarot scripts:', error);
      });

    return () => {
      cancelled = true;
      if (window.tarotApp) {
        window.tarotApp = null;
      }
      if (typeof window.cleanupTarotDeck === 'function') {
        window.cleanupTarotDeck();
      }
    };
  }, []);

  const threeCardJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is a 3-card tarot reading?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A 3-card tarot reading is a focused spread where you draw three cards representing your past, present, and future. It's one of the most popular tarot layouts because it gives clear, direct insight without being overwhelming."
        }
      },
      {
        "@type": "Question",
        "name": "What can I ask in a 3-card tarot reading?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can ask about anything — love, career, finances, personal growth, or general guidance. The Past Present Future spread works best with open-ended questions like 'What do I need to know about my career path?' or 'What's influencing my relationship right now?'"
        }
      },
      {
        "@type": "Question",
        "name": "How is a 3-card reading different from a Celtic Cross?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A 3-card reading offers quick, focused insight using just three cards. The Celtic Cross is a deeper 10-card spread that examines your situation from multiple angles including hopes, fears, external influences, and likely outcomes. Both are valuable — it depends on how much detail you want."
        }
      },
      {
        "@type": "Question",
        "name": "Is this 3-card tarot reading free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! This 3-card tarot reading is 100% free with no signup or credit card required. Draw as many readings as you like."
        }
      }
    ]
  };

  return (
    <div className="free-tarot-page">
      <Helmet>
        <title>Free 3-Card Tarot Reading Online - Past Present Future | NowTarot AI</title>
        <meta name="description" content="Get a free 3-card tarot reading online. Past, Present, and Future spread with instant AI-powered interpretations. 100% free, no signup required." />
        <meta name="keywords" content="3 card tarot reading, three card tarot, past present future tarot, free tarot reading, tarot spread, quick tarot reading" />
        <link rel="canonical" href="https://nowtarot.com/three-card-tarot/" />
        <meta property="og:title" content="Free 3-Card Tarot Reading - Past Present Future | NowTarot AI" />
        <meta property="og:description" content="Draw 3 cards for past, present, and future insight. 100% free, no signup required." />
        <meta property="og:image" content="https://nowtarot.com/og-three-card.png" />
        <meta property="og:url" content="https://nowtarot.com/three-card-tarot/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free 3-Card Tarot Reading - Past Present Future" />
        <meta name="twitter:description" content="Draw 3 cards for past, present, and future insight. 100% free." />
        <meta name="twitter:image" content="https://nowtarot.com/og-three-card.png" />
        <script type="application/ld+json">{JSON.stringify(threeCardJsonLd)}</script>
      </Helmet>
      <Header />
      <div className="stars"></div>
      <div className="container">
        <header className="tarot-header">
          <h1 className="page-title">3-Card Tarot Reading</h1>
        </header>

        <section className="intro-section">
          <p>
            Get quick, focused insight with a <strong>Past, Present &amp; Future</strong> tarot spread.
            Draw three cards to understand where you've been, where you are now, and where you're headed.
            100% free, no signup required.
          </p>
          <h2>How It Works</h2>
          <p>
            Think of your question or the area of life you'd like guidance on. Shuffle the deck, then pick
            three cards. The first card reveals <strong>past influences</strong>, the second shows your
            <strong> present situation</strong>, and the third points toward your <strong>future direction</strong>.
          </p>
        </section>

        <main className="main-content">
          <section className="deck-section visible" id="deckSection">
            
            <div className="spread-select-wrap" style={{ display: 'none' }}>
              <label htmlFor="spreadSelect">Choose your spread:</label>
              <select id="spreadSelect" className="spread-select">
              </select>
            </div>
            
            <p className="cards-needed" id="cardsNeeded">Please choose 3 Tarot cards for your reading!</p>
            
            <div className="options-inline" style={{ display: 'none' }}>
              <label className="toggle-label">
                <input type="checkbox" id="enableReversed" className="toggle-checkbox" />
                <span className="toggle-switch"></span>
                <span>Enable Reversed Cards</span>
              </label>
              <div className="slider-container" id="sliderContainer">
                <label htmlFor="reversedChance">Chance: <span id="chanceValue">25</span>%</label>
                <input type="range" id="reversedChance" min="5" max="50" defaultValue="25" className="slider" />
              </div>
            </div>
            
            <div id="cardarea-wrap" className="cardarea-wrap">
              <div id="cardarea">
              </div>
            </div>
            
            <p className="deck-instruction" id="deckInstruction"><em>Click on three cards for your reading!</em></p>
            
            <div className="deck-controls deck-controls-single">
              <button type="button" className="btn btn-reset" id="shuffle-again">
                Shuffle Again
              </button>
            </div>
            
            <button className="btn btn-interpret" id="interpretBtn" disabled style={{display: 'none'}}>
              ✨ Interpret Your Cards ✨
            </button>
          </section>

          <section className="interpretation-section" id="interpretationSection">
            <h3>Your Reading Interpretation</h3>
            <div className="interpretation-content" id="interpretationContent">
            </div>
            <button className="btn btn-new-reading" id="newReadingBtn">
              Start a New Reading
            </button>
          </section>

          <section className="cta-section ft-ask-band">
            <h3>Ask the AI tarot</h3>
            <p>Type a question. Get cards and an answer — no signup.</p>
            <AiAskBox onSignUp={() => setShowRegister(true)} />
          </section>

          <FeaturePromo variant="page" seed="/three-card-tarot/" exclude="/three-card-tarot/" />

          <section className="about-section">
            <h3>About 3-Card Tarot Spreads</h3>
            <div className="readings-info">
              <div className="reading-type">
                <h4>Past, Present &amp; Future</h4>
                <p>The classic 3-card layout. Understand how past events are influencing your present, and where your current path is leading. Great for any question or general guidance.</p>
              </div>
              <div className="reading-type">
                <h4>Mind, Body &amp; Spirit</h4>
                <p>Explore your mental state, physical well-being, and spiritual path. Ideal for checking in with yourself and restoring balance in your life.</p>
              </div>
              <div className="reading-type">
                <h4>Situation, Action &amp; Outcome</h4>
                <p>See the heart of your issue, the action you should take, and the likely result. Perfect when you're facing a specific decision or challenge.</p>
              </div>
            </div>
          </section>

          <section className="about-section">
            <h3>When to Use a 3-Card Spread</h3>
            <div className="readings-info">
              <div className="reading-type">
                <h4>Quick Daily Guidance</h4>
                <p>Start your day with a fast 3-card pull for insight into what the day holds. It takes just a minute and can set a powerful intention for your day ahead.</p>
              </div>
              <div className="reading-type">
                <h4>Specific Questions</h4>
                <p>Have a focused question about love, work, or a decision? Three cards give you a clear answer without the complexity of a larger spread.</p>
              </div>
              <div className="reading-type">
                <h4>New to Tarot?</h4>
                <p>A 3-card spread is the perfect starting point. It's easy to understand, quick to do, and still delivers meaningful guidance.</p>
              </div>
            </div>
          </section>

          <section className="faq-section">
            <h3>3-Card Tarot FAQ</h3>
            <div className="faq-list">
              <div className="faq-item">
                <h4>What is a 3-card tarot reading?</h4>
                <p>A 3-card tarot reading is a focused spread where you draw three cards representing your past, present, and future. It's one of the most popular tarot layouts because it gives clear, direct insight without being overwhelming.</p>
              </div>
              <div className="faq-item">
                <h4>What can I ask in a 3-card tarot reading?</h4>
                <p>You can ask about anything — love, career, finances, personal growth, or general guidance. The Past Present Future spread works best with open-ended questions like "What do I need to know about my career path?" or "What's influencing my relationship right now?"</p>
              </div>
              <div className="faq-item">
                <h4>How is a 3-card reading different from a Celtic Cross?</h4>
                <p>A 3-card reading offers quick, focused insight using just three cards. The Celtic Cross is a deeper 10-card spread that examines your situation from multiple angles including hopes, fears, external influences, and likely outcomes. Both are valuable — it depends on how much detail you want.</p>
              </div>
              <div className="faq-item">
                <h4>Is this 3-card tarot reading free?</h4>
                <p>Yes! This 3-card tarot reading is 100% free with no signup or credit card required. Draw as many readings as you like.</p>
              </div>
            </div>
          </section>

          <section className="about-section" style={{ textAlign: 'center' }}>
            <p>Looking for more depth? Try a <a href="/yes-or-no-tarot/" style={{ color: '#FFD700' }}>yes or no tarot</a> reading, a <a href="/love-tarot-reading/" style={{ color: '#FFD700' }}>love tarot reading</a>, or the full <a href="/celtic-cross-tarot/" style={{ color: '#FFD700' }}>10-card Celtic Cross</a> — all free, no signup required. Curious about a specific card? Browse <a href="/tarot-card-meanings/" style={{ color: '#FFD700' }}>tarot card meanings</a>.</p>
          </section>
        </main>

      </div>
      <Footer />
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}
    </div>
  );
};

export default ThreeCardTarot;
